import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
// Phone input
import PhoneInput from "react-phone-input-2";
import "react-phone-input-2/lib/bootstrap.css";
import { CountryDropdown, RegionDropdown } from "react-country-region-selector";
// Components
import RichTextEditor from "./RichTextEditor/RichTextEditor";
// Firebase
import { getStorage, ref, uploadBytes, getDownloadURL } from "firebase/storage";
import { getAuth, updateProfile } from "firebase/auth";
// Hooks
import { useFirestore } from "../hooks/useFirestore";
import { useAuthContext } from "../hooks/useAuthContext";

const ProfileForm = ({
  initialValues = {},
  isPending: externalPending = false,
  error: externalError = null,
  show = true,
  currentImage = null,
  success = null,
}) => {
  const { user } = useAuthContext();
  const { updateDocument, response } = useFirestore("users");

  const [form, setForm] = useState({
    email: "",
    password: "",
    name: "",
    phone: "",
    country: "",
    city: "",
    aboutMe: "",
    github: "",
    linkedin: "",
  });

  const [initialized, setInitialized] = useState(false);
  const [thumbnail, setThumbnail] = useState(null);
  const [thumbnailError, setThumbnailError] = useState("");
  const navigate = useNavigate();

  useEffect(() => {
    if (initialValues && !initialized) {
      setForm({
        email: initialValues.email || "",
        password: "",
        name: initialValues.name || "",
        phone: initialValues.phone || "",
        country: initialValues.country || "",
        city: initialValues.city || "",
        aboutMe: initialValues.aboutMe || "",
        github: initialValues.github || "",
        linkedin: initialValues.linkedin || "",
      });
      setInitialized(true);
    }
  }, [initialValues, initialized]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setForm((prev) => ({ ...prev, [name]: value }));
  };

  const handlePhoneChange = (value) => {
    setForm((prev) => ({ ...prev, phone: value }));
  };

  const handleCountryChange = (val) => {
    setForm((prev) => ({
      ...prev,
      country: val,
      city: "",
    }));
  };

  const handleCityChange = (val) => {
    setForm((prev) => ({
      ...prev,
      city: val,
    }));
  };

  const handleFileChange = (e) => {
    const selected = e.target.files[0];
    if (!selected) return setThumbnailError("Please select a file.");
    if (!selected.type.includes("image")) return setThumbnailError("File must be an image.");
    if (selected.size > 1000000) return setThumbnailError("Image must be under 1MB.");
    setThumbnailError("");
    setThumbnail(selected);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!currentImage && !thumbnail) {
      setThumbnailError("Profile image is required.");
      return;
    }

    let updatedForm = { ...form };

    if (thumbnail) {
      try {
        const storage = getStorage();
        const imageRef = ref(storage, `thumbnails/${user.uid}`);
        await uploadBytes(imageRef, thumbnail);
        const downloadURL = await getDownloadURL(imageRef);
        updatedForm.photoURL = downloadURL;

        const auth = getAuth();
        if (auth.currentUser) {
          await updateProfile(auth.currentUser, {
            photoURL: downloadURL,
          });
          setThumbnailError(null);
        }
      } catch (err) {
        setThumbnailError("Failed to upload image.");
        return;
      }
    }

    // Update Firestore user profile
    await updateDocument(user.uid, {
      ...updatedForm,
      lastModified: new Date(),
    });

    // Navigate to the profile page after successful update
    navigate("/profile/" + encodeURIComponent(form.name));
  };

  return (
    <form onSubmit={handleSubmit} className="p-4 bg-dark text-light rounded shadow">
      {/* Messages */}
      {(externalError || response.error) && (
        <div className="text-danger mb-3">{externalError || response.error}</div>
      )}
      {success && <div className="text-success mb-3">{success}</div>}

      {/* Name field, only shown if 'show' prop is true */}
      {show && (
        <div className="mb-3">
          <label className="form-label">Name</label>
          <input
            name="name"
            type="text"
            className="form-control bg-secondary text-light border-0"
            required
            value={form.name}
            onChange={handleChange}
          />
        </div>
      )}

      {/* Email */}
      <div className="mb-3">
        <label className="form-label">Email</label>
        <input
          name="email"
          type="email"
          className="form-control bg-secondary text-light border-0"
          required
          value={form.email}
          onChange={handleChange}
        />
      </div>

      {/* Password field, only shown if 'show' prop is true */}
      {show && (
        <div className="mb-3">
          <label className="form-label">Password</label>
          <input
            name="password"
            type="password"
            className="form-control bg-secondary text-light border-0"
            required
            value={form.password}
            onChange={handleChange}
          />
        </div>
      )}

      {/* Phone number */}
      <div className="mb-3">
        <label className="form-label">Phone Number</label>
        <PhoneInput
          country={"us"}
          value={form.phone}
          onChange={handlePhoneChange}
          inputProps={{
            name: "phone",
            required: true,
            className: "form-control bg-secondary text-light border-0",
          }}
        />
      </div>

      {/* Country */}
      <div className="mb-3">
        <label className="form-label">Country</label>
        <CountryDropdown
          value={form.country}
          onChange={handleCountryChange}
          className="form-select bg-secondary text-light border-0"
        />
      </div>

      {/* City */}
      <div className="mb-3">
        <label className="form-label">City</label>
        <RegionDropdown
          country={form.country}
          value={form.city}
          onChange={handleCityChange}
          className="form-select bg-secondary text-light border-0"
        />
      </div>

      {/* Profile pic */}
      <div className="mb-3">
        <label className="form-label">Profile Image</label>
        {currentImage && (
          <div className="mb-2">
            <img
              src={currentImage}
              alt="Current"
              className="img-thumbnail"
              style={{ width: "120px" }}
            />
          </div>
        )}
        <input
          type="file"
          className="form-control bg-secondary text-light border-0"
          onChange={handleFileChange}
        />
        {thumbnailError && <div className="text-danger mt-2">{thumbnailError}</div>}
      </div>

      {/* About me */}
      <div className="mb-3">
        <label className="form-label">About Me</label>
        <RichTextEditor
          value={form.aboutMe}
          onChange={(html) => setForm((prev) => ({ ...prev, aboutMe: html }))}
        />
      </div>

      {/* Linkedin */}
      <div className="mb-3">
        <label>Linkedin Link</label>
        <input
          name="linkedin"
          type="text"
          className="form-control bg-secondary text-light"
          value={form.linkedin}
          onChange={handleChange}
        />
      </div>

      {/* Github */}
      <div className="mb-3">
        <label>Github Link</label>
        <input
          name="github"
          type="text"
          className="form-control bg-secondary text-light"
          value={form.github}
          onChange={handleChange}
        />
      </div>

      {/* Submit button */}
      <div className="d-grid">
        <button className="btn btn-light" disabled={response.isPending || externalPending}>
          {response.isPending || externalPending ? "Submitting..." : "Save Changes"}
        </button>
      </div>
    </form>
  );
};

export default ProfileForm;
